import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Divider,
  Descriptions,
  Button,
  Typography,
} from 'antd';

import { useSelector } from 'react-redux';
import Select from 'components/Select';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { DATE_FRONTEND_FORMAT, toMoment } from 'utils/formatValues';
import { useLocation } from 'react-router-dom';
import {
  getQueja,
  getQuejas,
  patchQueja,
  baseURI,
} from 'api/solicitudes/quejas';
import {
  getCategoriasDeQuejas,
  getMotivosDeQuejas,
  getSubmotivos,
} from 'api/solicitudes';
import { EyeOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import ModalEvidencias from 'components/Catalogos/ModalEvidencias';
import Circle from 'components/Circle';
import { isMutable } from 'utils/estadosGlobales';

const { Title } = Typography;
const useQuery = () => new URLSearchParams(useLocation().search);

// Sort data by id in reverse order
const dataSorterReverse = (a, b) => b.id - a.id;

const SeguimientoQuejas = () => {
  const query = useQuery();
  const { estadosGlobales } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  // const [formTable] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);

  const [motivos, setMotivos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subMotivos, setSubMotivos] = useState([]);

  const [current, setCurrent] = useState({});

  const [visiblePhotos, setVisiblePhotos] = useState(false);
  const [mutable, setMutable] = useState(true);

  const getNameSolicitante = (record) => {
    const {
      tipo_cuenta_ciudadana,
      nombre_de_solicitante_de_queja,
      apellido_materno,
      apellido_paterno,
      contribuyente,
      ciudadano,
    } = record;
    let _solicitante = 'Anonimo';
    if (tipo_cuenta_ciudadana || ciudadano) {
      _solicitante = `${nombre_de_solicitante_de_queja
        || ''} ${apellido_paterno
        || ''} ${apellido_materno
        || ''}`.trim();
    } else if (contribuyente) {
      _solicitante = contribuyente;
    }
    return _solicitante;
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setCurrent(record);
    form.setFieldsValue({
      ...record,
      protestante: getNameSolicitante(record),
      correo_electronico: record.correo_de_solicitante,
      telefono: record.telefono_de_solicitante,
      fecha_de_los_hechos: record.fecha_de_los_hechos?.format('DD/MM/YYYY HH:mm a'),
    });
  };

  const onClickExpand = async (_match = null) => {
    setVisible(true);
    const [key] = selectedRowKeys;
    const match = _match ?? data.find((e) => e.id === key);
    let updated = match;
    if (match.estados_globales === 1) {
      updated = await patchQueja(key, { estados_globales: 2 }, false);
    }
    setMutable(isMutable(match.estados_globales));
    form.setFieldsValue({
      ...updated,
      protestante: getNameSolicitante(updated),
      correo_electronico: updated.correo_de_solicitante,
      telefono: updated.telefono_de_solicitante,
      fecha_de_los_hechos: updated.fecha_de_los_hechos?.format('DD/MM/YYYY HH:mm a'),
    });
  };

  const fetchData = async (q) => {
    setLoading(true);
    const id = parseInt(query.get('id'), 10);
    const _data = await getQuejas({ ...q });
    setData(_data.sort(dataSorterReverse));
    if (id) {
      const _current = await getQueja(id);
      handleOnRowClick(_current);
      onClickExpand();
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const [
        _data,
        _categorias,
        _motivos,
        _submotivos,
      ] = await Promise.all([
        getQuejas(),
        getCategoriasDeQuejas(),
        getMotivosDeQuejas(),
        getSubmotivos(),
      ]);

      if (mounted) {
        setData(_data.sort(dataSorterReverse));
        setCategorias(_categorias);
        setMotivos(_motivos);
        setSubMotivos(_submotivos);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
    setCurrent({});
    // setCurrentStep(0);
  };

  const periodosMap = Object.freeze({
    DIAS_HABILES: 1,
    MESES: 2,
    ANIOS: 3,
    NA: 4,
  });

  const formatsMapByPeriod = {
    [periodosMap.DIAS_HABILES]: 'd',
    [periodosMap.MESES]: 'M',
    [periodosMap.ANIOS]: 'y',
  };

  const renderCircle = (record) => {
    const plazo = record?.fecha_de_creacion || 1;
    const unit = formatsMapByPeriod[record.unidad_tiempo_respuesta];

    if (!unit) return <Circle />;
    const fechaInicio = toMoment(record.fecha_de_los_hechos);
    const expiryDate = toMoment(fechaInicio).add(plazo, unit);
    const diff = toMoment(new Date()).diff(expiryDate, 'days');
    if (diff < -1) {
      return <Circle backgroundColor="#59D948" borderColor="#2D9223" />;
    }
    if (diff > 0) {
      return <Circle backgroundColor="#E6242A" borderColor="#720A0D" />;
    }
    return <Circle backgroundColor="#FD6833" borderColor="#B44E2C" />;
  };

  const columns = [
    {
      titleText: 'Semáforo',
      width: 170,
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      render: (_, record) => (
        <Row style={{ width: '100%' }} justify="center" align="middle">
          {renderCircle(record)}
        </Row>
      ),
    },
    {
      titleText: 'Protesta Ciudadana',
      dataIndex: '_',
      key: '_',
      width: 300,
      render: (_, record) => {
        const { folio, categoria_de_la_queja } = record;
        return (
          <Descriptions column={1}>
            <Descriptions.Item label={<strong>Folio</strong>}>
              {folio}
            </Descriptions.Item>
            <Descriptions.Item label={(<strong>Tipo de protesta</strong>)}>
              {categorias.find((e) => e.id === categoria_de_la_queja)?.descripcion}
            </Descriptions.Item>
          </Descriptions>
        );
      },
    },
    {
      titleText: 'Protestante',
      dataIndex: '_',
      key: '_',
      width: 450,
      render: (_, record) => {
        const { correo_de_solicitante } = record;
        const _solicitante = getNameSolicitante(record);

        return (
          <Descriptions column={1}>
            <Descriptions.Item label={<strong>Solicitante</strong>}>
              {_solicitante}
            </Descriptions.Item>
            <Descriptions.Item label={(<strong>Correo electrónico</strong>)}>
              {correo_de_solicitante || ''}
            </Descriptions.Item>
          </Descriptions>
        );
      },
    },
    {
      titleText: 'Dependencia',
      dataIndex: 'oficina_de_queja',
      key: 'oficina_de_queja',
      width: 400,
    },
    {
      titleText: 'Trámite',
      dataIndex: 'tramite_de_queja',
      key: 'tramite_de_queja',
      width: 400,
    },
    {
      titleText: 'Fecha de registro',
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      width: 200,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT) || '',
    },
    {
      titleText: 'Fecha de los hechos',
      dataIndex: 'fecha_de_los_hechos',
      key: 'fecha_de_los_hechos',
      width: 200,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT) || '',
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
      width: 200,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const output = {
        estados_globales: values.estados_globales,
        observaciones_queja: values.observaciones_queja,
      };
      const saved = await patchQueja(key, output);
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Row align="center" justify="center" className="container">
        <Col xs={24} sm={24} md={24}>
          {!visible ? (
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              rowKey="id"
              mobileColIndex={0}
              handleOnRowClick={handleOnRowClick}
              allowAdd={false}
              allowEdit={false}
              allowDelete={false}
              allowSearch={false}
              controls={{
                onClickExpand: () => onClickExpand(),
              }}
              allowExpand
            />
          ) : (
            <Card
              title={(
                <FormSubmitControls
                  label={`Seguimiento de Solicitud - ${current?.folio || ''}`}
                  onCancel={onCancel}
                  onFinish={onFinish}
                  loading={loading}
                  allowAuthorize
                  allowCancel
                  mutable={mutable}
                  selectedRowKeys={selectedRowKeys}
                  baseURI={baseURI}
                  callback={(estados_globales) => {
                    const [key] = selectedRowKeys;
                    const normalized = [...data]
                      .map((e) => (e.id === key ? { ...e, estados_globales } : e));
                    setMutable(false);
                    setData(normalized);
                    const match = normalized.find((e) => e.id === key);
                    onClickExpand(match);
                  }}
                />
              )}
              bordered={false}
              bodyStyle={{ padding: '5px 10px' }}
            >
              <Form
                form={form}
                layout="vertical"
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Protestante"
                      name="protestante"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Correo Electrónico"
                      name="correo_electronico"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  {current?.telefono_de_solicitante && (
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Teléfono"
                      name="telefono"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  )}
                </Row>
                <Divider />
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="Categoría"
                      name="categoria_de_la_queja"
                    >
                      <Select
                        dataSource={categorias}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Fecha de los hechos"
                      name="fecha_de_los_hechos"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={10}>
                    <Form.Item
                      label="Dependencia"
                      name="oficina_de_queja"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Motivo"
                      name="motivo_de_la_queja"
                    >
                      <Select
                        dataSource={motivos}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Submotivo"
                      name="submotivo_de_la_queja"
                    >
                      <Select disabled dataSource={subMotivos} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Trámite"
                      name="tramite_de_queja"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Objeto de alteración o incumplimiento"
                      name="objeto_de_alteracion"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={16}>
                    <Form.Item
                      label="Dependencia"
                      name="oficina_de_queja"
                    >
                      <Input.TextArea
                        disabled
                        autoSize={{ minRows: 3, maxRows: 5 }}
                      />
                    </Form.Item>
                  </Col>
                  {/* Ver evidencias button */}
                  {current?.tiene_pruebas && (
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label=" "
                    >
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => setVisiblePhotos(true)}
                        block
                      >
                        Ver evidencias
                      </Button>
                    </Form.Item>
                  </Col>
                  )}
                </Row>
                <Divider
                  orientation="left"
                >
                  <Title level={4}>Seguimiento</Title>
                </Divider>
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="Estado de la solicitud"
                      name="estados_globales"
                    >
                      <Select
                        dataSource={estadosGlobales}
                        disabled={!mutable}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={16}>
                    <Form.Item
                      label="Comentarios"
                      name="observaciones_queja"
                    >
                      <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 5 }}
                        disabled={!mutable}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <ModalEvidencias
                visible={visiblePhotos}
                setVisible={setVisiblePhotos}
                queja={current.id}
                setLoading={setLoading}
              />
            </Card>
          )}
        </Col>
      </Row>
    </Spin>
  );
};

export default SeguimientoQuejas;
